import countAtoms from "./smilesAtomCounts.js";

/*
 * Sanity checks for a mechanism being defined on the submit page.
 *
 * These are advisory only - nothing here blocks a submission. They are meant to
 * catch the mistakes that actually occur (a mistyped SMILES, a step type that
 * does not match what happens between two molecules, a chain that loops back on
 * itself), so they must stay quiet whenever they cannot be sure: a molecule that
 * cannot be parsed, or that is not filled in yet, is skipped.
 */

/*
 * Step types after which the hydrogen count is expected to change. Matched as
 * case-insensitive substrings, so "protonation" also covers "deprotonation",
 * "protonation - class II" and "cyclopropanation deprotonation".
 *
 * The list is calibrated on release 2026-06-15: every one of the 3590 mechanism
 * steps in MARTS whose hydrogen count changes carries one of these types, except
 * for nine steps spread over types that never otherwise change it - which is
 * exactly what this check is meant to surface.
 */
export const HYDROGEN_CHANGING_STEP_TYPES = [
  "deprotonation",
  "protonation",
  "hydroxylation",
  "dehydration",
  "oxygen cyclization",
];

const explainsHydrogenChange = (stepType) =>
  HYDROGEN_CHANGING_STEP_TYPES.some((term) => (stepType || "").toLowerCase().includes(term));

/* What makes two entries in the chain the same molecule. */
function moleculeKey(molecule) {
  if (!molecule) return null;
  if (molecule.marts) return `marts:${molecule.marts}`;
  if (molecule.smiles) return `smiles:${molecule.smiles}|${molecule.stereochemistry || "uncertain"}`;
  return null;
}

function hydrogenDifference(after, before) {
  const count = Math.abs(after - before);
  return `${count} ${after > before ? "more" : "fewer"} hydrogen${count === 1 ? "" : "s"}`;
}

/**
 * @param {object} substrate      molecule the mechanism starts from
 * @param {Array}  steps          mechanism steps; `mechanism` is the type of the
 *                                arrow leading *into* `molecule`
 * @param {object} product        molecule the mechanism ends at
 * @param {string} finalStepType  type of the last arrow, into the product
 * @returns {string[]} one sentence per problem found, in chain order
 */
export default function getMechanismWarnings(substrate, steps = [], product, finalStepType = "") {
  const chain = [
    { label: "The substrate", molecule: substrate },
    ...steps.map((step, index) => ({
      label: `Intermediate ${index + 1}`,
      molecule: step?.molecule,
      stepType: step?.mechanism,
    })),
    { label: "The product", molecule: product, stepType: finalStepType },
  ];

  const warnings = [];

  for (let i = 1; i < chain.length; i++) {
    const previous = chain[i - 1];
    const current = chain[i];
    const before = countAtoms(previous.molecule?.smiles);
    const after = countAtoms(current.molecule?.smiles);
    if (!before || !after) continue;

    if (before.carbon !== after.carbon) {
      warnings.push(
        `${current.label} has ${after.carbon} carbons but ${previous.label.toLowerCase()} has ${before.carbon} - ` +
          `a mechanism step should not change the carbon count.`
      );
    }

    if (before.hydrogen !== after.hydrogen && !explainsHydrogenChange(current.stepType)) {
      const step = current.stepType
        ? `the step before it is "${current.stepType}"`
        : "no step type is set for the step before it";
      warnings.push(
        `${current.label} has ${hydrogenDifference(after.hydrogen, before.hydrogen)} than ` +
          `${previous.label.toLowerCase()}, but ${step} - expected a protonation, hydroxylation or dehydration step.`
      );
    }
  }

  const seen = new Map();
  for (const entry of chain) {
    const key = moleculeKey(entry.molecule);
    if (!key) continue;
    if (seen.has(key)) {
      const which = entry.molecule.name || entry.molecule.marts;
      warnings.push(
        `${entry.label} is the same molecule as ${seen.get(key).toLowerCase()}` +
          `${which ? ` (${which})` : ""} - a mechanism should not pass through the same molecule twice.`
      );
    } else {
      seen.set(key, entry.label);
    }
  }

  return warnings;
}
